import React, { type ReactElement, useState } from 'react';
import { styled } from '@compiled/react';
import {
	type PreloadedQuery,
	graphql,
	usePreloadedQuery,
	useQueryLoader,
	useFragment,
} from 'react-relay';
import { ModalTransition } from '@atlaskit/modal-dialog';
import type { TriggerProps } from '@atlaskit/popup';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue.graphql';
import type { ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersionsPopupContent_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersionsPopupContent_jiraIssue.graphql';
import uiMoveIssuesBetweenVersionsQuery, {
	type uiMoveIssuesBetweenVersionsQuery as uiMoveIssuesBetweenVersionsQueryType,
} from '@atlassian/jira-relay/src/__generated__/uiMoveIssuesBetweenVersionsQuery.graphql';
import { ISSUE_CARD_CLICK_IGNORE_TARGET } from '@atlassian/jira-software-releases-version-detail-issue-card-click-prevent-controller/src/common/constants.tsx';
import { VersionPicker } from '@atlassian/jira-software-releases-version-picker/src/ui/async.tsx';
import { VersionList } from './version-list/index.tsx';

export type RenderTrigger = (arg1: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	triggerProps: TriggerProps | Record<any, any>;
	isLoading: boolean;
	isSelected: boolean;
}) => ReactElement;

export type MoveIssuesBetweenVersionsProps = {
	renderTrigger: RenderTrigger;
	jiraIssue: ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue$key;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	queryReference: PreloadedQuery<uiMoveIssuesBetweenVersionsQueryType> | null | void;
	isOpen: boolean;
	onClose: () => void;
};

export type MoveIssuesBetweenVersionsPopupContentProps = {
	isVersionPickerModalOpen: boolean;
	jiraIssue: ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersionsPopupContent_jiraIssue$key;
	onClose: () => void;
	queryReference: PreloadedQuery<uiMoveIssuesBetweenVersionsQueryType>;
	setIsVersionPickerModalOpen: (isOpen: boolean) => void;
};

type UseMoveIssuesBetweenVersionsQueryType = (
	projectAri: string,
	first: number,
	versionAri: string,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
) => [PreloadedQuery<uiMoveIssuesBetweenVersionsQueryType> | null | void, () => void];

export const useMoveIssuesBetweenVersionsQuery: UseMoveIssuesBetweenVersionsQueryType = (
	projectAri: string,
	first: number,
	versionAri: string,
) => {
	const [queryRef, loadQuery] = useQueryLoader<uiMoveIssuesBetweenVersionsQueryType>(
		uiMoveIssuesBetweenVersionsQuery,
	);
	return [
		queryRef,
		() => loadQuery({ projectAri, first, versionAri }, { fetchPolicy: 'store-or-network' }),
	];
};

export const MoveIssuesBetweenVersionsPopupContent = ({
	queryReference,
	jiraIssue,
	onClose,
	isVersionPickerModalOpen,
	setIsVersionPickerModalOpen,
}: MoveIssuesBetweenVersionsPopupContentProps) => {
	const { jira } = usePreloadedQuery(
		graphql`
			query uiMoveIssuesBetweenVersionsQuery($projectAri: ID!, $first: Int!, $versionAri: ID!) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						... on JiraVersion {
							...versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersion
							...ui_softwareReleasesVersionPicker_VersionPicker_jiraVersion
						}
					}
					versionsForProject(jiraProjectId: $projectAri, first: $first, filter: [UNRELEASED])
						@required(action: THROW)
						@connection(key: "moveIssuesBetweenVersions_versionsForProject") {
						edges @required(action: THROW) {
							...versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersionEdges
						}
					}
				}
			}
		`,
		queryReference,
	);

	const issue = useFragment(
		graphql`
			fragment ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersionsPopupContent_jiraIssue on JiraIssue {
				...versionList_softwareReleasesMoveIssuesBetweenVersions_jiraIssue
				...ui_softwareReleasesVersionPicker_VersionPicker_jiraIssue
			}
		`,
		jiraIssue,
	);

	const { version, versionsForProject } = jira;

	const handleClose = () => {
		setIsVersionPickerModalOpen(false);
		onClose();
	};

	return (
		<>
			<VersionList
				jiraVersionEdges={versionsForProject.edges.filter(Boolean)}
				jiraIssue={issue}
				jiraVersion={version}
				onVersionPickerModalOpen={() => {
					setIsVersionPickerModalOpen(true);
				}}
			/>
			<ModalTransition>
				{isVersionPickerModalOpen && (
					<JSErrorBoundary
						id="jira.move.issues.between.versions.version.picker"
						packageName="jira-software-releases-move-issues-between-versions"
						teamName="nova"
						fallback="flag"
					>
						<ShortcutScope>
							<ModalDialog
								// This is used to ignore issue click when move issue modal is opened and anywhere is clicked in/outside of the modal
								testId="software-releases-move-issues-between-versions.ui.move-issue-version-select-modal"
								autoFocus={false}
								width="small"
								onClose={handleClose}
								messageId="software-releases-move-issues-between-versions.ui.modal-dialog"
								messageType="transactional"
							>
								<VersionPicker jiraVersion={version} jiraIssue={issue} onClose={handleClose} />
							</ModalDialog>
						</ShortcutScope>
					</JSErrorBoundary>
				)}
			</ModalTransition>
		</>
	);
};

export const MoveIssuesBetweenVersions = ({
	renderTrigger,
	jiraIssue,
	queryReference,
	isOpen,
	onClose,
}: MoveIssuesBetweenVersionsProps) => {
	const [isVersionPickerModalOpen, setIsVersionPickerModalOpen] = useState(false);

	const handleClose = () => {
		if (!isVersionPickerModalOpen) {
			onClose();
		}
	};

	const issue = useFragment(
		graphql`
			fragment ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				...ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersionsPopupContent_jiraIssue
			}
		`,
		jiraIssue,
	);

	return (
		<HoverVisibilityControlWrapper isVisible={isOpen}>
			<Popup
				isOpen={isOpen}
				autoFocus={false}
				onClose={handleClose}
				placement="left-start"
				messageId="software-releases-move-issues-between-versions.ui.popup"
				messageType="transactional"
				fallbackPlacements={['left-end']}
				content={() =>
					queryReference != null ? (
						<Placeholder name="move-issues-between-versions-popup-content" fallback={null}>
							<div data-version-details-issue-click-ignore-target={ISSUE_CARD_CLICK_IGNORE_TARGET}>
								<MoveIssuesBetweenVersionsPopupContent
									isVersionPickerModalOpen={isVersionPickerModalOpen}
									jiraIssue={issue}
									onClose={onClose}
									queryReference={queryReference}
									setIsVersionPickerModalOpen={setIsVersionPickerModalOpen}
								/>
							</div>
						</Placeholder>
					) : null
				}
				trigger={(triggerProps) =>
					renderTrigger({
						triggerProps,
						isSelected: isOpen,
						isLoading: false,
					})
				}
			/>
		</HoverVisibilityControlWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const HoverVisibilityControlWrapper = styled.div<{
	isVisible: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: (props) => (props.isVisible ? 'visible' : 'hidden'),
});
